import { NavbarModule } from '@/common/navbar/navbar.module';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { VindicationApiService } from '../vindication.api.service';
import { InternalStatusDto, TableContractor } from '../vindication.model';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InlineEditComponent } from '../components/inline-edit/inline-edit.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'app-contractor',
	standalone: true,
	imports: [
		MatTableModule,
		NavbarModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		InlineEditComponent,
		MatSortModule,
		MatButtonModule,
		MatExpansionModule,
		MatInputModule,
		FormsModule,
	],
	providers: [{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() }],
	templateUrl: './contractor.component.html',
	styleUrl: './contractor.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorComponent implements OnInit {
	readonly dataSource = new MatTableDataSource<TableContractor>([]);

	readonly length = signal(0);
	readonly pageSize = signal(10);
	readonly pageIndex = signal(0);
	readonly isLoading = signal(false);
	readonly search = signal<string>('');
	readonly internalStatusOptions = signal<InternalStatusDto[]>([]);

	readonly displayedColumns = [
		'shortName',
		'fullName',
		'prefix',
		'nip',
		'internalStatus',
		'mail',
		'phone',
		'note',
		'navigatorId',
		'contractorConfig',
	];

	private readonly dataCache = signal<{ [key: number]: TableContractor[] }>({});
	private readonly sortOrder = signal<Sort | null>(null);

	private readonly vindicationApiService = inject(VindicationApiService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly router = inject(Router);

	ngOnInit(): void {
		this.loadContractorInternalStatuses();
		this.loadContractors();
	}

	private loadContractorInternalStatuses(): void {
		this.vindicationApiService.getContractorInternalStatuses().subscribe((options) => {
			this.internalStatusOptions.set(options);
		});
	}

	onPageChange(event: PageEvent): void {
		this.pageIndex.set(event.pageIndex);

		if (this.pageSize() !== event.pageSize) {
			this.pageSize.set(event.pageSize);
			this.reloadContractors();
			return;
		}

		this.loadContractors();
	}

	onSortChange(sort: Sort): void {
		this.sortOrder.set(sort);
		this.reloadContractors();
	}

	loadContractors(): void {
		if (this.dataCache()[this.pageIndex()]) {
			this.dataSource.data = this.dataCache()[this.pageIndex()];
			return;
		}

		this.isLoading.set(true);

		const limit = this.pageSize();
		const offset = this.pageIndex() * this.pageSize();
		const sort = this.sortOrder();
		const ordering =
			sort?.direction === '' || !sort?.direction ? '' : `${sort.direction === 'desc' ? '-' : ''}${sort.active}`;
		let params = `?limit=${limit}&offset=${offset}`;
		if (ordering) {
			params += `&ordering=${ordering}`;
		}

		if (this.search()) {
			params += `&search=${this.search()}`;
		}

		this.vindicationApiService
			.getContractors(params)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((contractors) => {
				this.dataSource.data = contractors.results.map((contractor) => ({
					...contractor,
					mailLoader: false,
					phoneLoader: false,
					noteLoader: false,
					internalStatusLoader: false,
				}));

				this.dataCache.set({
					...this.dataCache(),
					[this.pageIndex()]: this.dataSource.data,
				});
				this.length.set(contractors.count);
			});
	}

	savePhone(contractor: TableContractor, newValue: string | string[]): void {
		if (Array.isArray(newValue)) {
			return;
		}
		contractor.phoneLoader = true;
		this.vindicationApiService
			.patchContractor(contractor.id, { phone: newValue })
			.pipe(
				finalize(() => {
					contractor.phoneLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				contractor.phone = updated.phone;
			});
	}

	saveMails(contractor: TableContractor, newMails: string | string[]): void {
		if (!Array.isArray(newMails)) {
			return;
		}
		contractor.mailLoader = true;
		this.vindicationApiService
			.patchContractor(contractor.id, { mails: newMails })
			.pipe(
				finalize(() => {
					contractor.mailLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				contractor.mails = updated.mails;
			});
	}

	saveNote(contractor: TableContractor, newValue: string | string[]): void {
		if (Array.isArray(newValue)) {
			return;
		}
		contractor.noteLoader = true;
		this.vindicationApiService
			.patchContractor(contractor.id, { note: newValue })
			.pipe(
				finalize(() => {
					contractor.noteLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				contractor.note = updated.note;
			});
	}

	saveInternalStatus(contractor: TableContractor, statusId: number | null): void {
		contractor.internalStatusLoader = true;
		this.vindicationApiService
			.patchContractor(contractor.id, { internalStatus: statusId })
			.pipe(
				finalize(() => {
					contractor.internalStatusLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				contractor.internalStatus = updated.internalStatus;
			});
	}

	goToContractorConfig(contractorId: number): void {
		const url = this.router.serializeUrl(this.router.createUrlTree(['vindication/contractors', contractorId]));

		window.open(url, '_blank', 'noopener');
	}

	onSearchEnterKey(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.reloadContractors();
		}
	}

	reloadContractors(): void {
		this.dataCache.set({});
		this.pageIndex.set(0);
		this.loadContractors();
	}
}
